import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Chip, Stack, TextField, Typography } from "@mui/material";
import EmployeeAutocomplete from "./EmployeeAutocomplete";
import { api } from "../../util/api";
import { notify } from "../../util/toast";
import { ds } from "../../styles/designSystem";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const C = ds.colors;

/**
 * 인원 선택 — <b>개인 검색 + 팀 전원 펼치기</b>를 한 칸에.
 *
 * <h3>어디서 왔나</h3>
 * <p>결재 열람자의 '팀으로 추가'({@code page/approval/create/ApprovalLineEditor.jsx})가 원형이다.
 * 그 화면이 실제 운영에서 다듬어 놓은 규칙을 그대로 가져왔고, 아래 주석의 «안 하면 이렇게 된다» 는
 * 전부 거기서 실제로 겪은 것들이다.
 *
 * <h3>팀을 저장하지 않고 사람으로 펼치는 이유</h3>
 * <p>선택 결과는 <b>사람 id 목록</b>이다. 부서를 통째로 걸어 두면 «지금 그 부서인 사람» 이 시점마다
 * 달라진다. 채널이라면 반년 뒤 입사한 신입이 과거 프로젝트의 자료 전체를 자동으로 보게 되고,
 * 결재라면 당시에 못 보던 사람이 열람자로 나타난다. 펼쳐서 넣으면 명단이 그 시점으로 고정된다.
 *
 * <h3>명부는 {@code GET /api/employees} 다</h3>
 * <p>권한 없이 부를 수 있는 재직자 공용 명부라 부서까지 함께 온다. 인사 관리용
 * {@code /api/employees/list} 를 쓰면 <b>권한 없는 사람의 화면이 통째로 403</b> 이 된다 —
 * 결재·채널 양쪽에서 같은 함정을 밟은 적이 있다.
 *
 * <h3>props</h3>
 * <ul>
 *   <li>{@code value} — 선택된 사람 배열 {@code [{id, name, deptName, positionName}]}</li>
 *   <li>{@code onChange(next)} — 배열 통째로</li>
 *   <li>{@code excludeIds} — 후보에서 뺄 사람(이미 참여 중·PM 등). 팀 펼치기에서도 빠진다.</li>
 *   <li>{@code excludeReason} — 위 사람들이 왜 빠지는지 한 줄(«PM 은 자동 포함» 등). 결과 문구에 붙는다.</li>
 *   <li>{@code personPlaceholder, teamPlaceholder}</li>
 * </ul>
 */
export default function TeamMemberPicker({
    value = [],
    onChange,
    excludeIds = [],
    excludeReason = "",
    personPlaceholder = "이름·이메일로 검색",
    teamPlaceholder = "팀으로 추가 (팀 전원)",
}) {
    const selectedIds = useMemo(() => new Set(value.map(v => v?.id).filter(v => v != null)), [value]);
    const excluded = useMemo(() => new Set((excludeIds || []).filter(v => v != null)), [excludeIds]);

    /* 재직자 공용 명부 — 팀 펼치기에만 쓴다(개인 검색은 EmployeeAutocomplete 가 서버 검색). */
    const [staff, setStaff] = useState([]);
    useEffect(() => {
        let alive = true;
        api.get(`${API_BASE}/employees`)
            .then((res) => {
                const data = res?.data ?? res ?? [];
                if (alive) setStaff(Array.isArray(data) ? data : []);
            })
            // 실패해도 화면은 그대로 쓸 수 있어야 한다 — 팀 칸만 빠지고 한 명씩 넣는 길은 남는다.
            .catch(() => { if (alive) setStaff([]); });
        return () => { alive = false; };
    }, []);

    /** 부서명 → 그 부서 재직자. 부서가 없는 직원은 묶을 기준이 없으므로 뺀다. */
    const staffByDept = useMemo(() => {
        const map = new Map();
        for (const e of staff) {
            const dept = e.department?.deptName ?? e.deptName;
            if (!dept) continue;
            if (!map.has(dept)) map.set(dept, []);
            map.get(dept).push(e);
        }
        return map;
    }, [staff]);

    /**
     * 팀 선택지 — 라벨에 <b>팀원 이름을 전부</b> 적는다.
     *
     * <p>«기획1팀 (4명)» 은 몇 명인지는 알려 주지만 <b>누가</b> 들어오는지는 알려 주지 않는다.
     * 그리고 라벨에 이름이 있으면 검색이 따라온다 — «노예원» 을 치면 그 사람이 속한 팀이 떠오른다.
     * 어느 팀인지 모를 때 사람들이 실제로 이렇게 찾는다.
     */
    const deptOptions = useMemo(() => (
        [...staffByDept.entries()]
            .map(([dept, members]) => ({
                value: dept,
                label: `${dept} · ${members.map(e => e.name).join(", ")}`,
                names: members.map(e => e.name),
            }))
            .sort((a, b) => a.value.localeCompare(b.value, "ko"))
    ), [staffByDept]);

    const toPerson = (e) => ({
        id: e.id,
        name: e.name,
        deptName: e.department?.deptName ?? e.deptName,
        positionName: e.position?.positionName ?? e.positionName,
    });

    /** 마지막 팀 추가 결과 한 줄. 다음 선택에서 덮어쓴다. */
    const [deptResult, setDeptResult] = useState("");
    /**
     * 고른 뒤 입력칸을 비우기 위한 상태.
     *
     * <p>MUI 는 선택하면 입력칸을 그 옵션 라벨로 채운다({@code reason === "reset"}). 그대로 두면
     * «경영본부 · 김한새, …» 가 남고, 그 텍스트가 다음 번엔 <b>필터</b>로 작동해 다른 팀이 목록에서
     * 사라진다 — 한 번 고르면 두 번째를 못 고른다. 이 칸은 값을 담는 곳이 아니라 <b>버튼</b>에 가깝다.
     */
    const [deptInput, setDeptInput] = useState("");

    const addDept = (deptName) => {
        const members = staffByDept.get(deptName) || [];
        const candidates = members.filter(e => !excluded.has(e.id));
        const added = candidates.filter(e => !selectedIds.has(e.id)).map(toPerson);
        if (added.length) onChange([...value, ...added]);

        /*
         * 결과를 한 줄로 알린다.
         *
         * 이미 전원이 들어가 있는 팀을 고르면 새로 넣을 사람이 없어 «화면이 아무것도 안 바뀐다».
         * 그러면 고른 사람은 «안 먹혔나» 하고 한 번 더 누른다. 그래서 몇 명이 새로 들어갔는지가
         * 아니라 <b>지금 몇 명이 들어가 있는지</b>를 말한다.
         */
        const skipped = members.length - candidates.length;
        setDeptResult(
            `${deptName} ${candidates.length}명이 참여 인력에 있습니다.`
            + (skipped > 0 ? ` (${excludeReason || "제외 대상"} ${skipped}명 제외)` : ""));
    };

    const removeOne = (id) => onChange(value.filter(v => v.id !== id));

    return (
        <Box>
            <EmployeeAutocomplete
                value={null}
                onChange={(emp) => {
                    if (!emp) return;
                    if (excluded.has(emp.id)) { notify(`${emp.name} 님은 이미 포함돼 있습니다`, "warning"); return; }
                    if (selectedIds.has(emp.id)) return;
                    onChange([...value, toPerson(emp)]);
                }}
                /* 이미 고른 사람·제외 대상은 후보에서 뺀다 — 골라 봐야 아무 일도 안 일어나는 항목이
                   목록에 남아 있으면 «왜 안 들어가지» 가 된다. */
                excludeIds={[...selectedIds, ...excluded]}
                placeholder={personPlaceholder}
                variant="outlined"
                disableUnderline={false}
            />

            {/* 팀 단위 추가 — 명부를 못 받았으면 칸 자체를 감춘다(빈 드롭다운은 고장으로 읽힌다). */}
            {staffByDept.size > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Autocomplete
                        size="small"
                        options={deptOptions}
                        value={null}
                        getOptionLabel={(o) => o?.label || ""}
                        isOptionEqualToValue={(o, v) => o?.value === v?.value}
                        onChange={(_, o) => { if (o) addDept(o.value); setDeptInput(""); }}
                        inputValue={deptInput}
                        // «reset» 은 무시한다 — 선택 직후 MUI 가 입력칸을 라벨로 채우려는 신호다(위 주석).
                        onInputChange={(_, v, reason) => {
                            if (reason === "input") setDeptInput(v);
                            else if (reason === "clear") setDeptInput("");
                        }}
                        /* 목록 높이를 묶어 둔다 — 이 칸은 보통 모달 아래쪽에 있어 MUI 가 목록을 «위로»
                           펼친다. 항목이 두 줄(팀명 + 이름들)이라 16개 팀이면 목록이 화면을 채우고,
                           바로 위의 선택된 사람 칩이 통째로 가려진다. */
                        ListboxProps={{ style: { maxHeight: 230 } }}
                        renderInput={(p) => <TextField {...p} placeholder={teamPlaceholder} />}
                        /* 목록은 두 줄로 — 팀 이름과 이름 목록을 한 줄에 이으면 어디까지가 팀명인지
                           눈이 매번 «·» 를 찾아야 한다. 이름은 줄바꿈을 허용해 잘리지 않게 둔다. */
                        renderOption={(props, o) => {
                            const { key, ...rest } = props;
                            return (
                                <li key={key} {...rest}>
                                    <Box sx={{ py: 0.3 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
                                            {o.value}
                                            <Box component="span" sx={{ ml: 0.6, fontWeight: 400, color: "#94a3b8" }}>
                                                {o.names.length}명
                                            </Box>
                                        </Typography>
                                        <Typography sx={{ fontSize: 11.5, color: "#64748b", whiteSpace: "normal" }}>
                                            {o.names.join(", ")}
                                        </Typography>
                                    </Box>
                                </li>
                            );
                        }}
                    />
                </Box>
            )}

            {deptResult && (
                <Typography sx={{ fontSize: 11.5, color: C.textMuted, mt: 0.6 }}>{deptResult}</Typography>
            )}

            {/* 선택된 사람 — 팀으로 넣은 뒤 여기서 개별로 뺀다. 이 조합이 «팀 전원 추가» 의 편의와
                «한 명만 빼기» 를 동시에 준다. */}
            {value.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                    {value.map(p => (
                        <Chip
                            key={p.id}
                            label={p.deptName ? `${p.name} · ${p.deptName}` : p.name}
                            size="small"
                            onDelete={() => removeOne(p.id)}
                            sx={{
                                fontSize: 11, height: 22, fontWeight: 500,
                                bgcolor: "#eff6ff", color: "#1e40af",
                                "& .MuiChip-deleteIcon": { color: "#1e40af" },
                            }}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
}
